import type { ProductListColumn } from "../../pages/products/Header";

export const normalizeProductUniqueId = (value: unknown): string =>
  String(value ?? "").trim();

export const CATALOGUE_INVENTORY_COLUMN_STORAGE_KEY =
  "hkp.catalogueAddInventory.visibleExtraColumns";

export const CATALOGUE_INVENTORY_COLUMNS: ProductListColumn[] = [
  { label: " ", field: "select", sort: false, width: 0.8, alwaysVisible: true },
  { label: "SrNo", field: "orgSrNo", sort: true, width: 1.2, alwaysVisible: true },
  { label: "Image", field: "logo", sort: false, width: 1.1, alwaysVisible: true },
  { label: "Product Name", field: "productName", sort: true, width: 6, alwaysVisible: true },
  { label: "Status", field: "status", sort: false, width: 1.6, alwaysVisible: true },
  { label: "Stock", field: "stock", sort: false, width: 1.6, alwaysVisible: true },
  { label: "DesignNo", field: "designNo", sort: true, width: 2.2 },
  { label: "Color", field: "color", sort: false, width: 2.2 },
  { label: "Design Owner", field: "designOwner", sort: true, width: 2.6 },
  { label: "Product Owner", field: "productOwner", sort: true, width: 2.6 },
  { label: "HKPtagNo", field: "srNo", sort: true, width: 1.8 },
  { label: "HKP Tag", field: "nfcTag", sort: true, width: 1.8 },
  { label: "Created at", field: "createdAt", sort: true, width: 2.2 },
];

export const OPTIONAL_CATALOGUE_INVENTORY_COLUMNS = CATALOGUE_INVENTORY_COLUMNS.filter(
  (column) => !column.alwaysVisible
);

const OPTIONAL_INVENTORY_FIELDS = new Set(
  OPTIONAL_CATALOGUE_INVENTORY_COLUMNS.map((column) => column.field)
);

export const parseStoredCatalogueInventoryColumns = (raw: string | null): string[] => {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (field) => typeof field === "string" && OPTIONAL_INVENTORY_FIELDS.has(field)
    );
  } catch {
    return [];
  }
};

export const visibleCatalogueInventoryColumns = (
  extraFields: string[]
): ProductListColumn[] => {
  const extra = new Set(extraFields);
  return CATALOGUE_INVENTORY_COLUMNS.filter(
    (column) => column.alwaysVisible || extra.has(column.field)
  );
};

export const collectProductUniqueIds = (payload: any): string[] => {
  const rows = Array.isArray(payload?.products)
    ? payload.products
    : Array.isArray(payload?.docs)
    ? payload.docs
    : [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const id = normalizeProductUniqueId(
      row?.productUniqueId || row?.productuniqueid
    );
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export const isAlreadyIncluded = (
  uid: unknown,
  membership: Set<string> | ReadonlySet<string>
): boolean => {
  const id = normalizeProductUniqueId(uid);
  return Boolean(id) && membership.has(id);
};

export const canSelectForCatalogue = (
  uid: unknown,
  membership: Set<string> | ReadonlySet<string>
): boolean => {
  const id = normalizeProductUniqueId(uid);
  if (!id) {
    return false;
  }
  return !membership.has(id);
};

export const productIdsToAdd = (
  selected: unknown[],
  membership: Set<string> | ReadonlySet<string>
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const raw of selected) {
    const id = normalizeProductUniqueId(raw);
    if (!canSelectForCatalogue(id, membership) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export type CatalogueAddEligibility = {
  productUniqueId: string;
  selectable: boolean;
  reasonCode: string;
};

export const canSelectEligibleProduct = (
  uid: unknown,
  membership: Set<string> | ReadonlySet<string>,
  eligibilityById: Record<string, CatalogueAddEligibility>
): boolean => {
  const id = normalizeProductUniqueId(uid);
  return canSelectForCatalogue(id, membership) && eligibilityById[id]?.selectable === true;
};

export const parseCatalogueAddEligibility = (
  response: any,
  requestedIds: string[]
): Record<string, CatalogueAddEligibility> | null => {
  const products = response?.data?.products;
  if (response?.success !== true || !Array.isArray(products)) return null;
  const requested = new Set(requestedIds);
  const result: Record<string, CatalogueAddEligibility> = {};
  for (const item of products) {
    const id = item?.productUniqueId;
    if (typeof id !== "string" || !requested.has(id) || result[id] ||
        typeof item?.selectable !== "boolean" || typeof item?.reasonCode !== "string") return null;
    result[id] = { productUniqueId: id, selectable: item.selectable, reasonCode: item.reasonCode };
  }
  return Object.keys(result).length === requested.size ? result : null;
};

const addReasonCopy: Record<string, string> = {
  ALREADY_INCLUDED: "Already in this catalogue.",
  PRODUCT_INACTIVE: "This product is inactive. Activate it in inventory before adding it.",
  PRODUCT_DELETED: "This product was removed from inventory and cannot be added.",
  NOT_OWNED_OR_UNAVAILABLE: "This product is not available for this studio to catalogue. Check its owner or ask a studio administrator.",
};

export const catalogueAddReason = (reasonCode: string): string =>
  addReasonCopy[reasonCode] || "This product cannot be added right now. Refresh inventory and try again.";

export const catalogueEligibilityError = (response: any): string => {
  if (response?.code === "CATALOGUE_NOT_FOUND") {
    return "This catalogue is no longer available. Return to the catalogue list and try again.";
  }
  if (response?.code === "SYSTEM_MANAGED") {
    return "This catalogue is managed automatically. Products cannot be added here.";
  }
  if (response?.code === "PERMISSION_DENIED") {
    return "You do not have permission to add products to this catalogue. Ask a studio administrator.";
  }
  return "We could not check which products can be added. Retry before selecting products.";
};

export const catalogueAddError = (response: any): string => {
  switch (response?.code) {
    case "PRODUCTS_NOT_ADDABLE":
      return "No products were added. Some selections changed or are not eligible. Review the reasons below and try again.";
    case "PRODUCTS_ALREADY_INCLUDED":
      return "No products were added because they are already in this catalogue. Refresh the catalogue to see them.";
    case "CATALOGUE_NOT_FOUND":
      return "This catalogue is no longer available. Return to the catalogue list and try again.";
    case "SYSTEM_MANAGED":
      return "This catalogue is managed automatically. Products cannot be added here.";
    case "PERMISSION_DENIED":
      return "You do not have permission to add products to this catalogue. Ask a studio administrator.";
    case "VALIDATION_FAILED":
      return "No products were added. Check the selected products and try again.";
    case "NETWORK_UNAVAILABLE":
      return "We could not confirm whether products were added. Refresh the catalogue before trying again.";
    default:
      return "We could not add these products. Refresh the catalogue and try again.";
  }
};
